/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Modal from "@mui/material/Modal";
import Zoom from "@mui/material/Zoom";
import { ModalLoadingCard } from "../utils/ModalLoadingCard";
import classes from "./css/recordReview.module.css";

/*
 * The hosting shell the review modals share: one Modal, one lg Container, and
 * the three states a record surface can be in - loading, failed, loaded.
 *
 * One shell rather than one per modal because the two are meant to be the
 * same surface on two entities: a steward moving between an ACCOUNT row and
 * a CONTACT row should not see the width, the entrance or the failure copy
 * change under them, and a copy is a second thing to keep in step.
 *
 * Every branch sits in a modalCardWrapper. The container is a flex row, so a
 * bare child hugs its content against the left edge and would jump to full
 * width the moment the record arrived; the Zoom entrance also needs a real
 * element to animate, and the cards are plain function components with no
 * ref to give it.
 */
export const ReviewModalShell = (props: {
  open: boolean;
  onClose: () => void;
  /**
   * Raised by the hosted card while a CRM write is in flight. Those writes
   * are irreversible and the request outlives the card, so the two
   * dismissals a steward can trigger by accident - a mis-aimed click on the
   * backdrop, a reflexive Escape - are withheld until it settles. Modal's
   * onClose only ever fires for those two, so nothing else is affected.
   */
  busy?: boolean;
  failed: boolean;
  failureMessage: string;
  onRetry: () => void;
  loadingTitle?: string;
  /** The loaded card; nothing (null/undefined) while the data is in flight. */
  children?: React.ReactNode;
}) => (
  <Modal
    open={props.open}
    onClose={() => {
      if (!props.busy) props.onClose();
    }}
    className={classes.modalRoot}
  >
    <Container maxWidth="lg" className={classes.modalContainer}>
      {props.failed ? (
        <div className={classes.modalCardWrapper}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={props.onRetry}>
                Try again
              </Button>
            }
          >
            {props.failureMessage}
          </Alert>
        </div>
      ) : props.children ? (
        <Zoom in>
          <div className={classes.modalCardWrapper}>{props.children}</div>
        </Zoom>
      ) : (
        <div className={classes.modalCardWrapper}>
          <ModalLoadingCard
            title={props.loadingTitle}
            onClose={props.onClose}
          />
        </div>
      )}
    </Container>
  </Modal>
);
